import type { AppNodeTypeWithData } from '@/nodes';
import { addingNodeState, selectedDraftFlowState } from '@/state';
import {
    type AppFlowParams,
    type AppNodeType,
    type DataKey,
    type EquipmentTypeId,
    getGuideType,
    type InfoTypeId,
    isNodeAllowedInGuide,
} from '@rock/types';
import { useSetAtom } from 'jotai';
import { useAtomValue } from 'jotai/index';
import { some } from 'lodash';
import { type DragEvent, type FC, useCallback } from 'react';

export const NODE_LIBRARY_OPTIONS: AppNodeTypeWithData[] = [
    {
        type: 'address-lookup',
        data: {
            type: 'address-lookup',
            pageTitle: 'Address Lookup',
            prompts: [
                {
                    text: "Enter your address and we'll look up your home details.",
                },
            ],
            dataKey: '' as DataKey,
            buttonLabel: 'Look Up',
        },
    },
    {
        type: 'entry',
        data: {
            type: 'entry',
        },
    },
    {
        type: 'exit',
        data: {
            type: 'exit',
        },
    },
    {
        type: 'return',
        data: {
            type: 'return',
            returnType: 'edge',
        },
    },
    {
        type: 'if',
        data: {
            type: 'if',
            field: '' as DataKey,
            op: '==',
            value: '',
        },
    },
    {
        type: 'set',
        data: {
            type: 'set',
            field: '' as DataKey,
            value: '',
        },
    },
    {
        type: 'create-equipment',
        data: {
            type: 'create-equipment',
            equipmentTypeId: '' as EquipmentTypeId,
            resetValuesAfterCreate: true,
            allowMultiple: false,
        },
    },
    {
        type: 'equipment-attribute',
        data: {
            type: 'equipment-attribute',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            equipmentTypeId: '' as EquipmentTypeId,
            fieldDataKey: '' as DataKey,
            buttonLabel: 'Continue',
            optional: false,
            helpType: 'none',
        },
    },
    {
        type: 'equipment',
        data: {
            type: 'equipment',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            equipmentTypeId: '' as EquipmentTypeId,
            buttonLabel: '',
            fieldDisplay: {
                display: 'all-fields',
            },
        },
    },
    {
        type: 'equipment-list',
        data: {
            type: 'equipment-list',
            pageTitle: '',
            grouping: 'none',
            emptyText: '',
            addLabel: '',
            doneLabel: '',
        },
    },
    {
        type: 'image',
        data: {
            type: 'image',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            equipmentTypeId: '' as EquipmentTypeId,
        },
    },
    {
        type: 'info',
        data: {
            type: 'info',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            buttonLabel: '',
        },
    },
    {
        type: 'input',
        data: {
            type: 'input',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            dataKey: '' as DataKey,
            infoTypeId: '' as InfoTypeId,
            optional: false,
            buttonLabel: 'Continue',
            helpType: 'none',
        },
    },
    {
        type: 'room-starter-selection',
        data: {
            type: 'room-starter-selection',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            dataKey: '' as DataKey,
            preselectRoomTypes: [],
            doneLabel: '',
        },
    },
    {
        type: 'initial-rooms',
        data: {
            type: 'initial-rooms',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            floorsDataKey: '' as DataKey,
            hasBasementDataKey: '' as DataKey,
            defaultRooms: [],
            preselectRooms: [],
            inputMode: 'simple' as const,
            doneLabel: '',
        },
    },
    {
        type: 'room',
        data: {
            type: 'room',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
        },
    },
    {
        type: 'scan',
        data: {
            type: 'scan',
            equipmentTypeId: '' as EquipmentTypeId,
        },
    },
    {
        type: 'select',
        data: {
            type: 'select',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            dataKey: '' as DataKey,
            // Select nodes have always shown Help Me Decide unless an author turned it off, so a new
            // one starts that way rather than making them pick it every time.
            helpType: 'help-me-decide',
            sections: [
                {
                    heading: '',
                    options: [
                        {
                            title: '',
                            value: '',
                            defaultSelected: false,
                        },
                    ],
                },
            ],
        },
    },
    {
        type: 'yes-no',
        data: {
            type: 'yes-no',
            pageTitle: '',
            prompts: [
                {
                    text: '',
                },
            ],
            dataKey: '' as DataKey,
            yesLabel: '',
            noLabel: '',
            noFirst: false,
            helpType: 'none',
        },
    },
    {
        type: 'sub-guide',
        data: {
            type: 'sub-guide',
            equipmentTypeId: '' as EquipmentTypeId,
            scopeKey: '' as DataKey,
        },
    },
];

const nodeTypeClasses: Record<string, string> = {
    'address-lookup': 'border-cyan-300 bg-cyan-100 text-black',
    entry: 'border-green-300 bg-green-100 text-black',
    exit: 'border-red-300 bg-red-100 text-black',
    return: 'border-yellow-300 bg-yellow-100 text-black',
    if: 'border-blue-300 bg-blue-100 text-black',
    set: 'border-purple-300 bg-purple-100 text-black',
    'create-equipment': 'border-orange-300 bg-orange-100 text-black',
    'sub-guide': 'border-teal-300 bg-teal-100 text-black',
    default: 'bg-slate-600 border-black text-white',
};

const NodeLibrary: FC<{ params: AppFlowParams }> = ({ params }) => {
    const setAddingNode = useSetAtom(addingNodeState);
    const selectedDraftFlowLoadable = useAtomValue(selectedDraftFlowState(params));
    const selectedDraftFlow = selectedDraftFlowLoadable.state === 'hasData' && selectedDraftFlowLoadable.data;
    const hasEntryNode = selectedDraftFlow && some(selectedDraftFlow.nodes, (n) => n.type === 'entry');

    const guideType = getGuideType(params);
    const filteredOptions = NODE_LIBRARY_OPTIONS.filter((option) =>
        isNodeAllowedInGuide(option.type as AppNodeType, guideType),
    );

    const onDragStart = useCallback(
        (event: DragEvent<HTMLDivElement>, nodeData: AppNodeTypeWithData) => {
            setAddingNode(nodeData);
            event.dataTransfer.effectAllowed = 'move';
        },
        [setAddingNode],
    );

    return (
        <div>
            <h4 className="text-md text-center mb-5">Node Library</h4>
            <div className="flex flex-row flex-wrap items-start gap-3">
                {filteredOptions.map((option) => {
                    const type = option.type;
                    const disabled = type === 'entry' && hasEntryNode;
                    const classes = nodeTypeClasses[type] || nodeTypeClasses.default;

                    return (
                        <div
                            key={type}
                            className={`border-1 rounded-lg text-sm uppercase p-3 ${disabled ? 'disabled opacity-50 cursor-not-allowed' : 'cursor-grab'} ${classes}`}
                            aria-disabled={disabled}
                            onDragStart={(event) => onDragStart(event, option)}
                            draggable={!disabled}
                        >
                            {type}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default NodeLibrary;
